import React, { useContext, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { makeGetMilestone } from '../selectors/milestones';
import { makeGetTaskGroup } from '../selectors/task-groups';

import { isTempId } from '../utils/helpers';
import { getMilestoneProgress, getMilestoneType, milestoneHasOverdueTasks, getMilestoneOpenTasksCount } from '../utils/milestone';

import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';
import { LocaleContext } from 'context/LocaleContext';

import MilestoneName from './milestone-name';
import DateTime from 'components/DateTime';
import GanttSideEntry, { GanttSideEntryMetaItem, GanttSideEntryTitleIcon } from 'components/GanttSideEntry';
import MilestoneActions from 'components/MilestoneActions';
import CustomFieldName from 'components/CustomFieldName';
import CustomFieldIcon from 'components/CustomFieldIcon';
import MilestoneTasks from 'components/MilestoneTasks';

import IconSubtask from '../icons/icon-subtask';
import IconCalendarSimple from '../icons/icon-calendar-simple';
import IconEye from '../icons/icon-eye';
import IconRepeat from '../icons/icon-repeat';
import IconArrowRight from '../icons/icon-arrow-right';
import IconArrowDown from '../icons/icon-arrow-down';

const MilestoneListItem = ({ id, projectId, name, description, placeholder, milestone, taskGroup, milestones, start, end, dependency, chartStartDate, dayWidth, expanded, rule, layout, selected, height, compactView, rowHeight, subrowHeight, publicPage, onChange, onDelete, onClick, onMouseEnter, onMouseLeave, onCollapse, onExpand, onTaskClick }) => {
    const rolesAndPermissions = useContext(RolesAndPermissionsContext);
    const localeCtx = useContext(LocaleContext);
    const { t } = useTranslation(['milestone', 'task', 'recurring', 'date']);

    const duration = useMemo(() => {
        return localeCtx?.differenceInDays(new Date(end), new Date(start)) + 1;
    }, [start, end, localeCtx]);

    const progress = useMemo(() => {
        return getMilestoneProgress(milestone);
    }, [milestone]);

    const type = useMemo(() => {
        return getMilestoneType(milestone);
    }, [milestone]);

    const hasOverdueTasks = useMemo(() => {
        return milestoneHasOverdueTasks(milestone);
    }, [milestone]);

    const count = useMemo(() => {
        return getMilestoneOpenTasksCount(milestone);
    }, [milestone]);

    const hidden = useMemo(() => {
        return (taskGroup || milestone).hidden;
    }, [taskGroup, milestone]);

    const handleClick = useCallback(() => {
        if (placeholder) {
            return;
        }

        onClick?.(id);
    }, [id, placeholder, onClick])

    const handleMouseEnter = useCallback(() => {
        onMouseEnter?.(id);
    }, [id, onMouseEnter]);

    const handleMouseLeave = useCallback(() => {
        onMouseLeave?.(id);
    }, [id, onMouseLeave]);

    const handleDelete = useCallback(() => {
        onDelete?.(id);
    }, [id, onDelete]);

    const handleMilestoneEdit = useCallback(nextProps => {
        onChange?.(id, nextProps);
    }, [id, onChange])

    const handleExpandToggle = useCallback(() => {
        if (expanded) {
            onCollapse(id);
        } else {
            onExpand(id);
        }
    }, [id, expanded, onCollapse, onExpand])

    const rollup = milestone?.rollup;

    return (
        <GanttSideEntry
            title={
                <>
                    {
                        id ? <MilestoneName id={id} /> : name
                    }

                    {
                        compactView && (count && onExpand) ? (
                            <div className="toggle-arrow" onClick={handleExpandToggle}>
                                { expanded ? <IconArrowDown /> : <IconArrowRight style={{ top: 1 }} /> }
                            </div>
                        ) : null
                    }
                </>
             }
            progress={progress}
            type={type}
            placeholder={placeholder}
            layout={layout}
            expanded={expanded}
            titleIcons={(
                <React.Fragment>
                    {
                        hidden?.length ? (
                            <GanttSideEntryTitleIcon tooltip={ t('Milestone hidden from', { roles: rolesAndPermissions?.getRolesListString(hidden, true) }) }>
                                <IconEye />
                            </GanttSideEntryTitleIcon>
                        ) : null
                    }

                    {
                        rule ? (
                            <GanttSideEntryTitleIcon tooltip={ t('recurring:Recurring') }>
                                <IconRepeat size="12px" />
                            </GanttSideEntryTitleIcon>
                        ) : null
                    }

                    <MilestoneActions
                        id={id}
                        className="title-icon-holder action"
                    />
                </React.Fragment>
            )}
            selected={selected}
            style={{ height }}
            mainHeight={rowHeight - 1}
            footer={
                expanded ? (
                    <MilestoneTasks
                        id={id}
                        taskHeight={subrowHeight}
                        compactView={compactView}
                        withTaskPlaceholder={true}
                        onTaskClick={onTaskClick}
                    />
                ) : null
            }
            onClick={handleClick}
        >
            {
                !compactView && start && end ? (
                    <GanttSideEntryMetaItem
                        icon={<IconCalendarSimple />}
                        title={(
                            <React.Fragment>
                                <DateTime format="date">{start}</DateTime> - <DateTime format="date">{end}</DateTime>

                                &nbsp;({ t('date:daysCount', { count: duration }) })
                            </React.Fragment>
                        )}
                    />
                ) : null
            }

            {
                compactView || (!count && publicPage) ? null : (
                    <GanttSideEntryMetaItem
                        icon={<IconSubtask />}
                        title={count ? t('task:openTasksCount', { count }) : <strong>{ `${t('task:Add a task')}...`}</strong>}
                        iconAfter={(count && onExpand) ? (expanded ? <IconArrowDown /> : <IconArrowRight />) : null}
                        onClick={(count && onExpand) ? handleExpandToggle : undefined}
                    />
                )
            }

            {
                rollup?.fieldId && !publicPage ? (
                    <GanttSideEntryMetaItem
                        icon={<CustomFieldIcon id={rollup.fieldId} />}
                        title={(
                            <React.Fragment>
                                <CustomFieldName id={rollup.fieldId} />:&nbsp;

                                <strong>
                                    <CustomFieldIcon id={rollup.fieldId} onlyIfType="budget" useCurrencySymbol={true} />

                                    { rollup.value }
                                </strong>
                            </React.Fragment>
                        )}
                    />
                ) : null
            }
        </GanttSideEntry>
    );

}


const makeMapStateToProps = () => {
    const getMilestone = makeGetMilestone();
    const getTaskGroup = makeGetTaskGroup();

    return ({ milestones, taskGroups }, ownProps) => {
        if (ownProps.placeholder) {
            return {};
        }
        const isTemp = isTempId(ownProps.id);

        const milestone = !isTemp && getMilestone(milestones.items, ownProps.id);

        return {
            isTemp,
            milestone,
            taskGroup: milestone && milestone.task_group && getTaskGroup(taskGroups.items, milestone.task_group),
        }
    }
}

export default connect(makeMapStateToProps)(MilestoneListItem);

